import { Box, styled } from '@mui/material';
import { useMemo } from 'react';

import {
  DataErrorsCountTile,
  DataErrorsList,
  InfoTile,
  LastUpdate,
} from '#entities';
import { bgMotifUrl, useManifest, type AppDataErrors } from '#shared';

const PREFIX = 'TSMED-ErrorsPage';
export const errorsPageClasses = {
  root: `${PREFIX}-root`,
  leftSide: `${PREFIX}-leftSide`,
  topInfo: `${PREFIX}-topInfo`,
  topInfoStatus: `${PREFIX}-topInfoStatus`,
  topInfoWarnings: `${PREFIX}-topInfoWarnings`,
  topInfoLastUpdate: `${PREFIX}-topInfoLastUpdate`,
  scrollArea: `${PREFIX}-scrollArea`,
} as const;

type RootProps = { insideWidget?: boolean };

const SystemMonitorStyled = styled(Box, {
  name: PREFIX,
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: prop => prop !== 'insideWidget',
})<RootProps>(
  ({ theme: { spacing, utils, breakpoints, palette }, insideWidget }) => ({
    [`&.${errorsPageClasses.root}`]: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: insideWidget ? 0 : 'var(--vh-full)',
      height: insideWidget ? '100%' : 'auto',
      overflow: insideWidget ? 'visible' : 'hidden',
      fontSize: utils.clamp(13, 24),
      lineHeight: 1.35,
    },

    [`.${errorsPageClasses.leftSide}`]: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: insideWidget ? 0 : spacing(3, 2, 2),

      backgroundImage: `url(${bgMotifUrl})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '70% 70%',

      [breakpoints.up('sm')]: {
        padding: insideWidget ? 0 : spacing(4, 3, 2.5),
      },
      [breakpoints.up('md')]: {
        padding: insideWidget ? 0 : spacing(5, 5, 3),
      },
      [breakpoints.up('lg')]: {
        padding: insideWidget ? 0 : spacing(6, 6, 3),
      },
      [breakpoints.up('xl')]: {
        padding: insideWidget ? 0 : spacing(7, 8, 3.5),
      },
      [breakpoints.up('2k')]: {
        padding: insideWidget ? 0 : spacing(8, 10, 4),
      },
      [breakpoints.up('4k')]: {
        padding: insideWidget ? 0 : spacing(10, 12, 5),
      },
    },

    // Sticky header that reorders via grid
    [`.${errorsPageClasses.topInfo}`]: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: palette.background.default,

      display: 'grid',
      gap: spacing(2),
      gridTemplateColumns: '1fr 1fr',
      gridTemplateAreas: `
      "status warnings"
      "lastupdate lastupdate"
    `,
      marginBottom: insideWidget ? 0 : spacing(2),

      [breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `
        "status"
        "warnings"
        "lastupdate"
      `,
        marginBottom: spacing(1),
      },
    },

    [`.${errorsPageClasses.topInfoStatus}`]: {
      gridArea: 'status',
    },
    [`.${errorsPageClasses.topInfoWarnings}`]: {
      gridArea: 'warnings',
    },
    [`.${errorsPageClasses.topInfoLastUpdate}`]: {
      gridArea: 'lastupdate',
    },

    [`.${errorsPageClasses.scrollArea}`]: {
      flexGrow: 1,
      overflowY: 'auto',
    },
  }),
);

interface ErrorsPageContentProps {
  dataErrors: AppDataErrors;
  networkErrors: AppDataErrors;
  lastUpdateTime: Date;
  backendNetworkError: string;
  insideWidget?: boolean;
}

export const SystemMonitor = ({
  dataErrors,
  networkErrors,
  lastUpdateTime,
  backendNetworkError = '',
  insideWidget = false,
}: ErrorsPageContentProps) => {
  useManifest();

  const dataErrorsCount = useMemo(() => {
    let count = 0;
    for (const objType in dataErrors) {
      if (dataErrors[objType]) {
        count += Object.keys(dataErrors[objType]).length;
      }
    }
    return count;
  }, [dataErrors]);

  const networkErrorsCount = useMemo(() => {
    let count = 0;
    for (const objType in networkErrors) {
      if (networkErrors[objType]) {
        count += Object.keys(networkErrors[objType]).length;
      }
    }
    return count;
  }, [networkErrors]);

  return (
    <SystemMonitorStyled
      className={errorsPageClasses.root}
      insideWidget={insideWidget}
    >
      <div className={errorsPageClasses.leftSide}>
        {/* sticky, responsive header */}
        <div className={errorsPageClasses.topInfo}>
          <div className={errorsPageClasses.topInfoStatus}>
            {backendNetworkError ? (
              <InfoTile
                icon="cloudError"
                largeCaption="BACKEND"
                smallCaption="NETWORK Error"
                additionalClassName="error"
                compact={insideWidget}
              />
            ) : (
              <InfoTile
                icon="cloudOk"
                largeCaption="BACKEND"
                smallCaption="NETWORK OK"
                compact={insideWidget}
              />
            )}
          </div>
          <div className={errorsPageClasses.topInfoLastUpdate}>
            <LastUpdate
              lastUpdateTime={lastUpdateTime}
              backendNetworkError={backendNetworkError}
              networkErrorsCount={networkErrorsCount}
              compact={insideWidget}
            />
          </div>
          <div className={errorsPageClasses.topInfoWarnings}>
            {dataErrorsCount ? (
              <DataErrorsCountTile
                dataErrorsCount={dataErrorsCount}
                backendNetworkError={backendNetworkError}
                compact={insideWidget}
              />
            ) : (
              <InfoTile
                icon="shield"
                largeCaption="ALL GOOD"
                smallCaption="SYSTEM STATUS"
                compact={insideWidget}
              />
            )}
          </div>
        </div>

        {/* scrollable content */}
        <div className={errorsPageClasses.scrollArea}>
          <DataErrorsList
            backendNetworkError={backendNetworkError}
            networkErrors={networkErrors}
            dataErrors={dataErrors}
            compact={insideWidget}
          />
        </div>
      </div>
    </SystemMonitorStyled>
  );
};
